import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { type DietaryPreferencesTypes } from '../../machines';
import { getProductDietaryRestrictionsNames } from '../../utils';
import { AllergenOrDietaryProperty } from '../../utils/dietary-preferences';

/**
 * Returns a function that generates the "Contains {restrictions}" label based on provided restrictions.
 */
export const useDietaryRestrictions = (props: Props) => {
  const { isAllergensEnabled, restrictions } = props;

  const { formatMessage } = useIntl();

  return useCallback(
    (params: Params) => {
      const { allergenProperties, dietaryProperties } = params;
      const properties = isAllergensEnabled ? allergenProperties : dietaryProperties;

      const activeDietaryRestrictions = getProductDietaryRestrictionsNames(
        restrictions,
        properties,
      );

      if (!activeDietaryRestrictions) return;

      return formatMessage(messages.containsRestrictions, {
        restrictions: activeDietaryRestrictions,
      });
    },
    [isAllergensEnabled, restrictions, formatMessage],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  containsRestrictions: {
    defaultMessage: 'Contains {restrictions}',
    description: 'Dietary Restrictions | Contains restrictions',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isAllergensEnabled: boolean;
  restrictions: DietaryPreferencesTypes;
};

type Params = {
  allergenProperties: readonly AllergenOrDietaryProperty[] | undefined;
  dietaryProperties: readonly AllergenOrDietaryProperty[] | undefined;
};
